import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ErrorBoundary } from './ErrorBoundary';
import { Auth } from './Auth';
import { AlertTriangle, Shield, User, RefreshCw } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  fallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  fallback 
}) => {
  const { user, profile, loading, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  // Handle sign out with error handling
  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      // Force navigation even if sign out fails
      navigate('/');
    } finally {
      setIsSigningOut(false);
    }
  };

  // Show loading during authentication check
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show login form
  if (!user) {
    return <Auth onAuthSuccess={() => navigate('/dashboard')} />;
  }
  
  // If admin is required but user is not admin
  if (requireAdmin && !isAdmin) {
    return fallback || (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 text-center">
            <div className="p-4 bg-red-400/10 rounded-full inline-block mb-6">
              <Shield className="h-12 w-12 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Accesso Negato</h2>
            <p className="text-gray-400 mb-6">
              Non hai i permessi necessari per accedere a questa sezione. 
              Solo gli amministratori possono gestire i database.
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 mb-6">
              <User className="h-4 w-4" />
              <span>Ruolo attuale: {profile?.role || 'user'}</span>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Torna alla Dashboard
              </button>
              <button
                onClick={handleSignOut}
                disabled={isSigningOut}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isSigningOut ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <span>Esci</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If everything is ok, show protected content
  return (
    <ErrorBoundary fallback={
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 text-center">
            <div className="p-4 bg-red-400/10 rounded-full inline-block mb-6">
              <AlertTriangle className="h-12 w-12 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Errore Applicazione</h2>
            <p className="text-gray-400 mb-6">
              Si è verificato un errore durante il caricamento di questa sezione.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
              >
                Torna alla Dashboard
              </button>
              <button
                onClick={handleSignOut}
                disabled={isSigningOut}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isSigningOut ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <span>Esci</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    }>
      {children}
    </ErrorBoundary>
  );
};